import { Routes, Route, Navigate } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import { LoadingSpinner } from '@/shared/components/ui/LoadingSpinner';
import { ROUTES } from '@/shared/config/routes.config';
import { ProtectedRoute } from '@/features/auth/components/ProtectedRoute';
import { PublicRoute } from '@/features/auth/components/PublicRoute';
import { AdminProtectedRoute } from '@/features/admin/components/AdminProtectedRoute';

// Import components directly to avoid lazy loading issues
import LandingPage from '@/features/landing/pages/LandingPage';
import AboutPage from '@/features/about/pages/AboutPage';
import BenefitsInfoPage from '@/features/benefits/pages/BenefitsInfoPage';
const LoginPage = lazy(() => import('@/features/auth/pages/LoginPage'));
const RegisterPage = lazy(() => import('@/features/auth/pages/RegisterPage'));
const DashboardPage = lazy(() => import('@/features/dashboard/pages/DashboardPage'));
const CalculatorPage = lazy(() => import('@/features/calculator/pages/CalculatorPage'));
const ChatPage = lazy(() => import('@/features/chat/pages/ChatPage'));
const IntelligencePage = lazy(() => import('@/features/intelligence/pages/IntelligencePage'));
const ProfilePage = lazy(() => import('@/features/profile/pages/ProfilePage'));
const PrivacyPage = lazy(() => import('@/features/privacy/pages/PrivacyPage'));
const AdminDashboard = lazy(() => import('@/features/admin/pages/AdminDashboard'));
const NotFoundPage = lazy(() => import('@/shared/pages/NotFoundPage'));
const ForbiddenPage = lazy(() => import('@/shared/pages/ForbiddenPage'));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <LoadingSpinner size="lg" label="Loading page..." />
  </div>
);

export const AppRouter = () => {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public routes */}
        <Route path={ROUTES.HOME} element={<LandingPage />} />
        <Route path={ROUTES.ABOUT} element={<AboutPage />} />
        <Route path={ROUTES.BENEFITS} element={<BenefitsInfoPage />} />
        
        <Route 
          path={ROUTES.AUTH.LOGIN} 
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          } 
        />
        <Route 
          path={ROUTES.AUTH.REGISTER} 
          element={
            <PublicRoute>
              <RegisterPage />
            </PublicRoute>
          } 
        />

        {/* Protected routes */}
        <Route 
          path={ROUTES.DASHBOARD} 
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path={`${ROUTES.CALCULATOR.INDEX}/*`} 
          element={<CalculatorPage />}
        />
        <Route 
          path={`${ROUTES.CHAT.INDEX}/*`} 
          element={<ChatPage />} 
        />
        <Route 
          path={`${ROUTES.INTELLIGENCE.INDEX}/*`} 
          element={<IntelligencePage />} 
        />
        <Route 
          path={`${ROUTES.PROFILE.INDEX}/*`} 
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path={`${ROUTES.PRIVACY_MANAGEMENT.INDEX}/*`} 
          element={<PrivacyPage />} 
        />

        {/* Admin routes (protected - admin access only) */}
        <Route 
          path={`${ROUTES.ADMIN.INDEX}/*`} 
          element={
            <AdminProtectedRoute>
              <AdminDashboard />
            </AdminProtectedRoute>
          } 
        />

        {/* Help routes */}
        <Route path={ROUTES.HELP.INDEX} element={<Navigate to={ROUTES.HELP.FAQ} replace />} />
        <Route path={ROUTES.HELP.FAQ} element={<NotFoundPage />} />
        <Route path={ROUTES.HELP.GUIDES} element={<NotFoundPage />} />
        <Route path={ROUTES.HELP.CONTACT_SUPPORT} element={<NotFoundPage />} />

        {/* Error pages */}
        <Route path={ROUTES.ERROR.NOT_FOUND} element={<NotFoundPage />} />
        <Route path={ROUTES.ERROR.FORBIDDEN} element={<ForbiddenPage />} />
        <Route path={ROUTES.ERROR.UNAUTHORIZED} element={<Navigate to={ROUTES.AUTH.LOGIN} replace />} />
        <Route path={ROUTES.ERROR.SERVER_ERROR} element={<NotFoundPage />} />
        <Route path={ROUTES.ERROR.MAINTENANCE} element={<NotFoundPage />} />

        {/* Privacy Policy and Terms */}
        <Route path={ROUTES.PRIVACY} element={<NotFoundPage />} />
        <Route path={ROUTES.TERMS} element={<NotFoundPage />} />
        <Route path={ROUTES.CONTACT} element={<NotFoundPage />} />

        {/* Auth route redirects */}
        <Route path={ROUTES.AUTH.FORGOT_PASSWORD} element={<Navigate to={ROUTES.AUTH.LOGIN} replace />} />
        <Route path={ROUTES.AUTH.RESET_PASSWORD} element={<Navigate to={ROUTES.AUTH.LOGIN} replace />} />
        <Route path={ROUTES.AUTH.VERIFY_EMAIL} element={<Navigate to={ROUTES.AUTH.LOGIN} replace />} />
        <Route path={ROUTES.AUTH.MFA_SETUP} element={<Navigate to={ROUTES.AUTH.LOGIN} replace />} />

        
        {/* 404 page */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
};